import { FeatureRadio } from '@/components/chariot-ui';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Modal } from '@/components/Modal';
import { useCustomerTypeHandlers } from '@/sections/customerManagement/hooks/actions';
import {
  ActionSource,
  CustomerData,
} from '@/sections/customerManagement/types';
import { useGlobalState } from '@/state/global.state';
import { CUSTOMER_TYPE_LABELS, CustomerTypeValue } from '@/types';

interface UpdateCustomerTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: CustomerData[];
  source?: ActionSource;
  onSuccess?: () => void;
}

/**
 * Legacy props interface for backward compatibility
 * @deprecated Use UpdateCustomerTypeModalProps with customers array
 */
interface LegacyUpdateCustomerTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: CustomerData;
}

const CUSTOMER_TYPE_OPTIONS: {
  value: CustomerTypeValue;
  label: string;
  description: string;
}[] = [
  {
    value: 'ENGAGEMENT',
    label: CUSTOMER_TYPE_LABELS.ENGAGEMENT,
    description: 'Professional Services engagement',
  },
  {
    value: 'FREEMIUM',
    label: CUSTOMER_TYPE_LABELS.FREEMIUM,
    description: 'Free tier customer with limited features',
  },
  {
    value: 'MANAGED',
    label: CUSTOMER_TYPE_LABELS.MANAGED,
    description: 'Managed subscription with full-service support',
  },
  {
    value: 'PILOT',
    label: CUSTOMER_TYPE_LABELS.PILOT,
    description: 'Customer in pilot program testing features',
  },
  {
    value: 'SAAS',
    label: CUSTOMER_TYPE_LABELS.SAAS,
    description: 'SaaS subscription with self-service access',
  },
  {
    value: 'BUG_BOUNTY',
    label: CUSTOMER_TYPE_LABELS.BUG_BOUNTY,
    description: 'Bug bounty program',
  },
];

/**
 * Update Customer Type Modal
 *
 * Supports both single customer updates (from drawer) and bulk updates (from table).
 * Automatically detects context based on customers array length.
 */
export const UpdateCustomerTypeModal: React.FC<
  UpdateCustomerTypeModalProps | LegacyUpdateCustomerTypeModalProps
> = props => {
  // Handle legacy props format for backward compatibility
  const normalizedProps: UpdateCustomerTypeModalProps =
    'customer' in props
      ? {
          isOpen: props.isOpen,
          onClose: props.onClose,
          customers: [props.customer],
          source: 'drawer',
          onSuccess: undefined,
        }
      : props;

  const {
    isOpen,
    onClose,
    customers,
    source = 'drawer',
    onSuccess,
  } = normalizedProps;

  const { drawerState } = useGlobalState();
  const { handleUpdateCustomerType, isPending } = useCustomerTypeHandlers();

  // Determine if this is a bulk operation
  const isBulk = customers.length > 1;
  const customer = customers[0];

  const [selectedType, setSelectedType] = useState<CustomerTypeValue | null>(
    customer?.type || null
  );

  const handleUpdate = async () => {
    if (!selectedType) {
      toast.error('Please select a customer type');
      return;
    }

    try {
      const result = await handleUpdateCustomerType(customers, selectedType);

      // Update global customer state if drawer is open for this customer
      if (
        source === 'drawer' &&
        drawerState.customer?.email === customer.email
      ) {
        drawerState.setCustomer({
          ...drawerState.customer,
          type: selectedType,
        });
      }

      // Show appropriate success message
      if (result.failed === 0) {
        toast.success(
          isBulk
            ? `Updated type for ${result.succeeded} customers`
            : `Customer type updated successfully for ${customer.displayName}`
        );
      } else if (result.succeeded > 0) {
        toast.warning(
          `Updated type for ${result.succeeded} customer${result.succeeded > 1 ? 's' : ''}, ` +
            `but failed for ${result.failed} customer${result.failed > 1 ? 's' : ''}`
        );
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error('Failed to update customer type');
      console.error('[UpdateCustomerTypeModal] Update failed:', error);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={isBulk ? 'Update Customer Type (Bulk)' : 'Update Customer Type'}
      subtitle={
        isBulk
          ? `Select a new type for ${customers.length} customers`
          : `Select a new customer type for ${customer?.displayName}`
      }
      size="lg"
      footer={{
        text: isPending ? 'Updating...' : 'Submit',
        onClick: handleUpdate,
        disabled: !selectedType || isPending,
      }}
    >
      <div className="flex flex-col gap-4">
        {isBulk && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-900/20">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              This will update the type for {customers.length} customers. This
              action cannot be undone.
            </p>
          </div>
        )}

        {!isBulk && customer?.type && (
          <div className="bg-chariot-background-secondary rounded-md p-3">
            <p className="text-chariot-text-secondary text-sm">
              Current Type:{' '}
              <span className="text-chariot-text-primary font-medium">
                {CUSTOMER_TYPE_LABELS[customer.type] || 'Not Set'}
              </span>
            </p>
          </div>
        )}

        <CustomerTypeOptions
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          isPending={isPending}
        />
      </div>
    </Modal>
  );
};

export const CustomerTypeOptions = ({
  selectedType,
  setSelectedType,
  isPending,
}: {
  selectedType: CustomerTypeValue | null;
  setSelectedType: (type: CustomerTypeValue | null) => void;
  isPending: boolean;
}) => {
  return (
    <div className="space-y-3">
      {CUSTOMER_TYPE_OPTIONS.map(option => (
        <FeatureRadio
          key={option.value}
          id={option.value}
          title={option.label}
          description={option.description}
          checked={selectedType === option.value}
          onChange={() => setSelectedType(option.value)}
          disabled={isPending}
          className="border-chariot-stroke/30 hover:border-chariot-brand-primary/50 w-full rounded-xl transition-colors"
        />
      ))}
    </div>
  );
};
